import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/stores/auth';
import { Button } from '@/components/ui/Button';

/**
 * 条款补同意横幅（批1 合规包 + P2-7 双 docType）：非阻断。terms 优先;
 * terms 绿而提供者协议落后(仅提供者)时显示提供者协议变体。「我已阅读」按
 * 当前版本补记对应 docType 并隐藏;失败保持展示,下次进入重试。
 */
export default function TermsConsentBanner() {
  const { t } = useTranslation('common');
  const { user } = useAuthStore();
  const [variant, setVariant] = useState<'terms' | 'provider_agreement' | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api
      .get('/api/users/me/consent')
      .then((s: { needsAccept?: boolean; providerAgreement?: { needsAccept?: boolean } }) => {
        if (cancelled) return;
        if (s.needsAccept) setVariant('terms');
        else if (s.providerAgreement?.needsAccept) setVariant('provider_agreement');
        else setVariant(null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!variant) return null;

  async function acknowledge() {
    setBusy(true);
    try {
      // terms 走默认(空 body 兼容旧后端);provider_agreement 显式声明
      await api.post('/api/users/me/consent', variant === 'terms' ? {} : { docType: variant });
      setVariant(null);
    } catch {
      // 保留横幅，下次进入重试
    } finally {
      setBusy(false);
    }
  }

  const isTerms = variant === 'terms';

  return (
    <div className="mb-4 flex items-center gap-3 rounded-2xl border border-border bg-surface-1 px-4 py-3">
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
      <p className="min-w-0 flex-1 text-sm text-muted-foreground">
        {t(isTerms ? 'consentBannerText' : 'paBannerText')}{' '}
        <Link to={isTerms ? '/terms' : '/provider-agreement'} className="text-brand-600 hover:underline dark:text-brand-400">
          {t(isTerms ? 'consentBannerLink' : 'paBannerLink')}
        </Link>
      </p>
      <Button size="sm" variant="outline" onClick={acknowledge} disabled={busy} className="shrink-0">
        {t(isTerms ? 'consentBannerAck' : 'paBannerAck')}
      </Button>
    </div>
  );
}
